'use client'
import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'

/**
 * Hero background — "routes converging on the hub."
 *
 * Several graceful curved lines (brand navy + maroon) sweep in from the edges
 * of the region and meet at a single point: the MRN hub (Tehran). They draw in
 * together as one slow, coordinated gesture — breathe in over ~12s, hold as the
 * hub blooms, fade, repeat — so it reads as one calm event rather than a swarm
 * of moving objects to track. Designed (clean ink-lines), not lit, so it lives
 * happily on the cream surface.
 *
 * Legibility: the arcs fade where they cross the headline zone (an ellipse over
 * the upper-centre) and strengthen as they near the hub in the lower third —
 * "converging energy" that also keeps the copy the star. Reduced-motion draws a
 * single static frame; the loop pauses when hidden or scrolled offscreen.
 *
 * Brand colours, hard-coded (canvas can't read CSS vars):
 *   navy #003049 · maroon #801424 · cream comes from the section behind.
 */

const NAVY = '0, 48, 73'
const MAROON = '128, 20, 36'
const CYCLE = 12000 // ms for one breathe-in / hold / fade cycle
const SAMPLES = 56 // points sampled along each curve

type Line = { sx: number; sy: number; cx: number; cy: number; navy: boolean; delay: number }

export default function HeroNetwork() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    let running = true
    let w = 0
    let h = 0
    let hub = { x: 0, y: 0 }
    let lines: Line[] = []
    // Headline-zone ellipse — arcs fade inside it so the copy stays dominant.
    let ell = { x: 0, y: 0, rx: 1, ry: 1 }

    // 0 deep inside the headline zone → 1 well outside it.
    const calm = (x: number, y: number) => {
      const e = ((x - ell.x) / ell.rx) ** 2 + ((y - ell.y) / ell.ry) ** 2
      if (e >= 1) return 1
      const f = (e - 0.25) / 0.75
      return f < 0 ? 0 : f
    }

    function build() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = canvas!.clientWidth
      h = canvas!.clientHeight
      canvas!.width = Math.round(w * dpr)
      canvas!.height = Math.round(h * dpr)
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)

      hub = { x: w * 0.5, y: h * 0.74 }
      ell = { x: w * 0.5, y: h * 0.42, rx: w * 0.42, ry: h * 0.24 }

      // Sources fan across the top / upper sides; arcs bow outward into the hub.
      const sources = [
        [0.04, 0.30],
        [0.17, 0.09],
        [0.37, 0.03],
        [0.5, 0.015],
        [0.63, 0.04],
        [0.83, 0.09],
        [0.96, 0.31],
      ]
      lines = sources.map(([fx, fy], i) => {
        const sx = w * fx
        const sy = h * fy
        const mx = (sx + hub.x) / 2
        const my = (sy + hub.y) / 2
        // Perpendicular bow, pushed away from centre so arcs splay gracefully.
        const dx = hub.x - sx
        const dy = hub.y - sy
        const len = Math.hypot(dx, dy) || 1
        const sign = sx < hub.x ? -1 : 1
        const bow = 0.2 * len * sign
        return {
          sx,
          sy,
          cx: mx + (-dy / len) * bow,
          cy: my + (dx / len) * bow,
          navy: i % 2 === 0,
          delay: (i % 4) * 0.05, // gentle stagger
        }
      })
    }

    // Quadratic bezier point at u∈[0,1].
    const bez = (a: number, c: number, b: number, u: number) => {
      const v = 1 - u
      return v * v * a + 2 * v * u * c + u * u * b
    }

    function drawLine(l: Line, reveal: number, env: number) {
      if (reveal <= 0 || env <= 0) return
      const upto = reveal * (SAMPLES - 1)
      const col = l.navy ? NAVY : MAROON
      ctx!.lineWidth = 1.5
      ctx!.lineCap = 'round'
      let px = l.sx
      let py = l.sy
      for (let j = 1; j <= SAMPLES - 1; j++) {
        if (j > upto + 1) break
        const u = Math.min(j, upto) / (SAMPLES - 1)
        const x = bez(l.sx, l.cx, hub.x, u)
        const y = bez(l.sy, l.cy, hub.y, u)
        // Energy converges: faint at the source, stronger toward the hub.
        const grad = 0.2 + 0.8 * u
        const a = grad * calm(x, y) * env * 0.72
        if (a > 0.004) {
          ctx!.strokeStyle = `rgba(${col}, ${a})`
          ctx!.beginPath()
          ctx!.moveTo(px, py)
          ctx!.lineTo(x, y)
          ctx!.stroke()
        }
        px = x
        py = y
      }
    }

    function drawHub(intensity: number) {
      const a = calm(hub.x, hub.y)
      // Soft bloom as the routes arrive.
      const r = 40
      const g = ctx!.createRadialGradient(hub.x, hub.y, 0, hub.x, hub.y, r)
      g.addColorStop(0, `rgba(${MAROON}, ${0.16 * intensity * a})`)
      g.addColorStop(1, `rgba(${MAROON}, 0)`)
      ctx!.fillStyle = g
      ctx!.beginPath()
      ctx!.arc(hub.x, hub.y, r, 0, Math.PI * 2)
      ctx!.fill()
      // Steady node.
      ctx!.fillStyle = `rgba(${MAROON}, ${0.8 * a})`
      ctx!.beginPath()
      ctx!.arc(hub.x, hub.y, 3.5, 0, Math.PI * 2)
      ctx!.fill()
    }

    function render(phase: number) {
      ctx!.clearRect(0, 0, w, h)
      // Envelope: hold full, then fade over the last 22% before the loop.
      const env = phase < 0.78 ? 1 : 1 - (phase - 0.78) / 0.22
      let arrived = 0
      for (const l of lines) {
        const reveal = Math.max(0, Math.min(1, (phase - l.delay) / 0.32))
        drawLine(l, reveal, env)
        arrived += reveal
      }
      drawHub((arrived / lines.length) * env)
    }

    function frame(time: number) {
      if (!running) return
      render(((time % CYCLE) + CYCLE) % CYCLE / CYCLE)
      raf = requestAnimationFrame(frame)
    }

    function renderStatic() {
      ctx!.clearRect(0, 0, w, h)
      for (const l of lines) drawLine(l, 1, 1)
      drawHub(1)
    }

    build()

    if (reduce) {
      renderStatic()
      const onResize = () => {
        build()
        renderStatic()
      }
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }

    const start = () => {
      if (!raf) raf = requestAnimationFrame(frame)
    }
    const stop = () => {
      cancelAnimationFrame(raf)
      raf = 0
    }
    // Run only when the hero is BOTH on-screen and the tab is visible. Track
    // each signal independently and derive `running` from both via sync(), so
    // the IntersectionObserver and visibilitychange handlers can't disagree —
    // e.g. re-focusing the tab must not resume an animation that's scrolled
    // off-screen (which would burn CPU until the next scroll).
    let onscreen = true
    const sync = () => {
      running = onscreen && !document.hidden
      if (running) start()
      else stop()
    }
    const onResize = () => {
      stop()
      build()
      sync()
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        onscreen = entry.isIntersecting
        sync()
      },
      { threshold: 0 },
    )
    io.observe(canvas)
    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', sync)
    sync()

    return () => {
      stop()
      io.disconnect()
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [reduce])

  return <canvas ref={canvasRef} aria-hidden className="absolute inset-0 h-full w-full" />
}
